import React, {useState, useMemo, useEffect} from 'react';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import {useTranslation} from 'react-i18next';
import {
    SourceDestinationTypeValues,
    ServiceTypeValues,
    SourceDestinationZoneValues,
    ServiceCustomTypeValues,
    ApplicationTypeValues,
    ServiceOrAppiValues,
    ActionValues,
    LogValues,
    CpRuleSchema
} from '../../../api/DBSchema/CPRule';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import SMCButton from '../../../components/Simple/SMCButton';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import EmptyRow from '../../../components/Simple/EmptyRow';
import SMCModal from '../../../components/core/SMCModal';
import {SMCCheckbox} from '../../../components/Simple/SMCCheckbox';
import SMCLink from '../../../components/Simple/SMCLink';
import Api from '../../../api/SMPFetchHelper';
import {useBoolean, useSMCSnackbar} from '../../../utils/hooks';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {SMCRadio} from '../../../components/Simple/SMCRadio';
import AppiEditorModal from '../AppiEditorModal';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import UpdatableObjectSelectModal from '../UpdatableObjectSelectModal';
import {
    ACCESS_POLICY_OUTGOING,
    searchFunction
} from '../../EditorPages/AbstractObjects/GatewayPlan/InnerPages/BaseAccessPolicy/util';

function AccessPolicyNextGenBaseEditorModal({
                                                baseObjectName,
                                                isOpen,
                                                width = 710,
                                                height = 720,
                                                onCancel,
                                                onSuccess,
                                                defaultObject,
                                                objectType,
                                                appiData,
                                                uoTableData,
                                                zonesData,
                                                incomingOrOutgoing,
                                                setAppiDataValue,
                                                setUoTableDataValue
                                            }) {
    const {t} = useTranslation();
    const {enqueueErrorSnackbar} = useSMCSnackbar();
    const {getErrorMessage} = useSMCErrorHandler();

    const [isAppiEditorOpen, openAppiEditor, closeAppiEditor] = useBoolean(false);
    const [isUoSelectionOpen, openUoSelection, closeUoSelection] = useBoolean(false);

    const isOutgoing = (incomingOrOutgoing === ACCESS_POLICY_OUTGOING) ? true : false;

    const sourceDestinationAny = useMemo(() => {
        return isOutgoing ?
            {
                sourceDestinationType: SourceDestinationTypeValues.INTERNET,
                sourceDestinationTypeAnyStr: 'SMP.PLAN.SEC_SOFT_BLADES.INTERNET'
            } :
            {
                sourceDestinationType: SourceDestinationTypeValues.ANY,
                sourceDestinationTypeAnyStr: 'SMP.PLAN.SEC_SOFT_BLADES.ANY'
            };

    }, [isOutgoing]);

    const sourceOptions = useMemo(() => (
        <>
            <SMCOption value={SourceDestinationTypeValues.ANY}>{t('SMP.PLAN.SEC_SOFT_BLADES.ANY')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.HOST}>{t('Single IP address')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IP_RANGE}>{t('IP address range')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.NETWORK}>{t('SMP.NETWORK')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IPV6_HOST}>{t('Single IPv6 address')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IPV6_RANGE}>{t('IPv6 address range')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IPV6_NETWORK}>{t('IPv6 network')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.ZONE}>{t('SMP.PLAN.SEC_SOFT_BLADES.ZONE')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.DOMAIN}>{t('Domain Name')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.UPDATABLE_OBJECTS}>{t('Updatable Objects')}</SMCOption>
        </>
    ), [t]);

    const zoneTypeOptions = useMemo(() => {
        if (zonesData) {
            const zonesDataTranslated = zonesData.map(zone => ({...zone, zoneName: t(zone.zoneName)}))
            return zonesDataTranslated.map(zone => <SMCOption value={zone.zoneCode}>{zone.zoneName}</SMCOption>);
        } else {
            return [];
        }
    }, [t, zonesData]);

    const destinationOptions = useMemo(() => (
        <>
            <SMCOption
                value={sourceDestinationAny.sourceDestinationType}>{t(sourceDestinationAny.sourceDestinationTypeAnyStr)}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.HOST}>{t('Single IP address')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IP_RANGE}>{t('IP address range')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.NETWORK}>{t('SMP.NETWORK')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IPV6_HOST}>{t('Single IPv6 address')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IPV6_RANGE}>{t('IPv6 address range')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.IPV6_NETWORK}>{t('IPv6 network')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.ZONE}>{t('SMP.PLAN.SEC_SOFT_BLADES.ZONE')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.DOMAIN}>{t('Domain Name')}</SMCOption>
            <SMCOption value={SourceDestinationTypeValues.UPDATABLE_OBJECTS}>{t('Updatable Objects')}</SMCOption>
        </>
    ), [sourceDestinationAny, t]);

    const serviceTypeOptions = useMemo(() => (
        <>
            <SMCOption value={ServiceTypeValues.CUSTOM}>{t('SMP.PLAN.SEC_SOFT_BLADES.CUSTOM')}</SMCOption>
            <SMCOption value={ServiceTypeValues.ANY}>{t('SMP.PLAN.SEC_SOFT_BLADES.ANY')}</SMCOption>
            <SMCOption value={ServiceTypeValues.ANY_TCP}>{'Any TCP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.ANY_TCP_UDP}>{'TCP/UDP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.ANY_UDP}>{'Any UDP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.CIFS}>{'CIFS'}</SMCOption>
            <SMCOption value={ServiceTypeValues.DELAY_SENSITIVE_SERVICES}>{'Delay_Sensitive_Services'}</SMCOption>
            <SMCOption value={ServiceTypeValues.DHCP}>{'DHCP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.DNS}>{'DNS'}</SMCOption>
            <SMCOption value={ServiceTypeValues.DNS_TCP}>{'DNS_TCP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.DNS_UDP}>{'DNS_UDP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.FTP}>{'FTP'}</SMCOption>
            <SMCOption
                value={ServiceTypeValues.GUARANTEED_BANDWIDTH_SERVICES}>{'Guaranteed_Bandwidth_Services'}</SMCOption>
            <SMCOption value={ServiceTypeValues.HTTP}>{'HTTP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.HTTPS}>{'HTTPS'}</SMCOption>
            <SMCOption value={ServiceTypeValues.ICMP}>{'ICMP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.IMAP}>{'IMAP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.LDAP}>{'LDAP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.MAIL}>{'Mail'}</SMCOption>
            <SMCOption value={ServiceTypeValues.NETBIOS}>{'NetBios'}</SMCOption>
            <SMCOption value={ServiceTypeValues.POP3}>{'POP3'}</SMCOption>
            <SMCOption value={ServiceTypeValues.SIP}>{'SIP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.SIP_TCP}>{'SIP_TCP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.SIP_UDP}>{'SIP_UDP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.SMTP}>{'SMTP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.SNMP}>{'SNMP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.SSH}>{'SSH'}</SMCOption>
            <SMCOption value={ServiceTypeValues.TELNET}>{'TELNET'}</SMCOption>
            <SMCOption value={ServiceTypeValues.TFTP}>{'TFTP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.VOIP}>{'VoIP'}</SMCOption>
            <SMCOption value={ServiceTypeValues.WEB_BROWSING}>{'Web Browsing'}</SMCOption>
        </>
    ), [t]);

    const serviceCustomTypeOptions = useMemo(() => (
        <>
            <SMCOption value={ServiceCustomTypeValues.TCP}>{'TCP'}</SMCOption>
            <SMCOption value={ServiceCustomTypeValues.UDP}>{'UDP'}</SMCOption>
            <SMCOption value={ServiceCustomTypeValues.ICMP}>{'ICMP'}</SMCOption>
            <SMCOption value={ServiceCustomTypeValues.OTHER}>{t('SMP.PLAN.SEC_SOFT_BLADES.OTHER')}</SMCOption>
        </>
    ), [t]);

    const applicationTypeOptions = useMemo(() => (
        <>
            <SMCOption value={ApplicationTypeValues.ANY}>{t('SMP.PLAN.SEC_SOFT_BLADES.ANY')}</SMCOption>
            <SMCOption value={ApplicationTypeValues.PREDEFINED}>{t('SMP.PLAN.SEC_SOFT_BLADES.PREDEFIND')}</SMCOption>
            <SMCOption value={ApplicationTypeValues.CUSTOM_URL}>{t('SMP.PLAN.SEC_SOFT_BLADES.CUSTOM_URL')}</SMCOption>
        </>
    ), [t]);

    const [sourceType, setSourceType] = useState(SourceDestinationTypeValues.ANY);
    const [sourceSingleIp, setSourceSingleIp] = useState('');
    const [sourceStartIp, setSourceStartIp] = useState('');
    const [sourceEndIp, setSourceEndIp] = useState('');
    const [sourceNetworkIp, setSourceNetworkIp] = useState('');
    const [sourceNetworkSubnetMask, setSourceNetworkSubnetMask] = useState('');
    const [sourceZoneType, setSourceZoneType] = useState(SourceDestinationZoneValues.USERS);
    const [sourceSingleIpV6, setSourceSingleIpV6] = useState('');
    const [sourceStartIpV6, setSourceStartIpV6] = useState('');
    const [sourceEndIpV6, setSourceEndIpV6] = useState('');
    const [sourceNetworkIpV6, setSourceNetworkIpV6] = useState('');
    const [sourceNetworkIpV6Prefix, setSourceNetworkIpV6Prefix] = useState('');
    const [sourceIpV6ObjectName, setSourceIpV6ObjectName] = useState('');
    const [sourceUoCode, setSourceUoCode] = useState('');

    const [destinationType, setDestinationType] = useState(sourceDestinationAny.sourceDestinationType);
    const [destinationSingleIp, setDestinationSingleIp] = useState('');
    const [destinationStartIp, setDestinationStartIp] = useState('');
    const [destinationDomain, setDestinationDomain] = useState('');
    const [destinationDomainObject, setDestinationDomainObject] = useState('');
    const [sourceDomain, setSourceDomain] = useState('');
    const [sourceDomainObject, setSourceDomainObject] = useState('');
    const [destinationEndIp, setDestinationEndIp] = useState('');
    const [destinationNetworkIp, setDestinationNetworkIp] = useState('');
    const [destinationNetworkSubnetMask, setDestinationNetworkSubnetMask] = useState('');
    const [destinationSingleIpV6, setDestinationSingleIpV6] = useState('');
    const [destinationStartIpV6, setDestinationStartIpV6] = useState('');
    const [destinationEndIpV6, setDestinationEndIpV6] = useState('');
    const [destinationeNetworkIpV6, setDestinationNetworkIpV6] = useState('');
    const [destinationNetworkIpV6Prefix, setDestinationNetworkIpV6Prefix] = useState('');
    const [destinationIpV6ObjectName, setDestinationIpV6ObjectName] = useState('');
    const [destinationZoneType, setDestinationZoneType] = useState(SourceDestinationZoneValues.USERS);
    const [destinationUoCode, setDestinationUoCode] = useState('');

    const [serviceOrAppi, setServiceOrAppi] = useState(ServiceOrAppiValues.SERVICE);

    const [serviceType, setServiceType] = useState(ServiceTypeValues.ANY);
    const [serviceCustomType, setServiceCustomType] = useState(ServiceCustomTypeValues.OTHER);
    const [servicePorts, setServicePorts] = useState('');
    const [serviceIcmpCode, setServiceIcmpCode] = useState('');
    const [serviceIcmpType, setServiceIcmpType] = useState('');
    const [serviceIpProtocol, setServiceIpProtocol] = useState('');

    const [applicationType, setApplicationType] = useState(ApplicationTypeValues.ANY);

    const [isUoEditingSourceOrDestination, setIsUoEditingSourceOrDestination] = useState("SOURCE");
    const [applicationPredefinedAppId, setApplicationPredefinedAppId] = useState('');
    const [applicationCustomUrlValue, setApplicationCustomUrlValue] = useState('');
    const [applicationCustomUrlName, setApplicationCustomUrlName] = useState('');
    const [applicationCustomUrlIsRegex, setApplicationCustomUrlIsRegex] = useState('');

    const [action, setAction] = useState(ActionValues.BLOCK);
    const [log, setLog] = useState(LogValues.LOG);
    const [description, setDescription] = useState('');
    const [enabled, setEnabled] = useState(true);


    const sourceDestinationSchema = CpRuleSchema.sourceDestinationSchema;
    const serviceSchema = CpRuleSchema.serviceSchema;
    const applicationSchema = CpRuleSchema.applicationSchema;


    const applicationName = useMemo(() => {
        if(!applicationPredefinedAppId){
            return "";
        }

        let appName=applicationPredefinedAppId;
        if (appiData && appiData.length>0) {
                const obj = appiData.find((obj) => obj.id === applicationPredefinedAppId);
                if (obj) {
                    appName= obj.name;
                }
            }
        else{
            appName= defaultObject.application.applicationName;
        }
        return appName;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appiData, applicationPredefinedAppId]);


    const onFinish = async () => {
        const source = {
            [sourceDestinationSchema.type]: sourceType,
            [sourceDestinationSchema.zone]: sourceZoneType,
            [sourceDestinationSchema.ipSingle]: sourceSingleIp,
            [sourceDestinationSchema.ipStart]: sourceStartIp,
            [sourceDestinationSchema.ipEnd]: sourceEndIp,
            [sourceDestinationSchema.ipNetwork]: sourceNetworkIp,
            [sourceDestinationSchema.ipMask]: sourceNetworkSubnetMask,
            [sourceDestinationSchema.ipV6Single]: sourceSingleIpV6,
            [sourceDestinationSchema.ipV6Start]: sourceStartIpV6,
            [sourceDestinationSchema.ipV6End]: sourceEndIpV6,
            [sourceDestinationSchema.ipV6Network]: sourceNetworkIpV6,
            [sourceDestinationSchema.ipV6Prefix]: sourceNetworkIpV6Prefix,
            [sourceDestinationSchema.ipV6ObjectName]: sourceIpV6ObjectName,
            [sourceDestinationSchema.domain]: sourceDomain,
            [sourceDestinationSchema.domainObject]: sourceDomainObject,
            [sourceDestinationSchema.updatableObjectCode]: sourceUoCode

        }

        const destination = {
            [sourceDestinationSchema.type]: destinationType,
            [sourceDestinationSchema.zone]: destinationZoneType,
            [sourceDestinationSchema.ipSingle]: destinationSingleIp,
            [sourceDestinationSchema.domain]: destinationDomain,
            [sourceDestinationSchema.domainObject]: destinationDomainObject,
            [sourceDestinationSchema.ipStart]: destinationStartIp,
            [sourceDestinationSchema.ipEnd]: destinationEndIp,
            [sourceDestinationSchema.ipNetwork]: destinationNetworkIp,
            [sourceDestinationSchema.ipMask]: destinationNetworkSubnetMask,
            [sourceDestinationSchema.ipV6Single]: destinationSingleIpV6,
            [sourceDestinationSchema.ipV6Start]: destinationStartIpV6,
            [sourceDestinationSchema.ipV6End]: destinationEndIpV6,
            [sourceDestinationSchema.ipV6Network]: destinationeNetworkIpV6,
            [sourceDestinationSchema.ipV6Prefix]: destinationNetworkIpV6Prefix,
            [sourceDestinationSchema.ipV6ObjectName]: destinationIpV6ObjectName,
            [sourceDestinationSchema.updatableObjectCode]: destinationUoCode

        }

        const service = {
            [serviceSchema.type]: serviceType,
            [serviceSchema.customType]: serviceCustomType,
            [serviceSchema.ports]: servicePorts,
            [serviceSchema.icmpType]: serviceIcmpType,
            [serviceSchema.icmpCode]: serviceIcmpCode,
            [serviceSchema.ipProtocol]: serviceIpProtocol
        }

        const application = {
            [applicationSchema.type]: applicationType,
            [applicationSchema.predefinedAppId]: applicationPredefinedAppId,
            [applicationSchema.customUrlValue]: applicationCustomUrlValue,
            [applicationSchema.customUrlName]: applicationCustomUrlName,
            [applicationSchema.customUrlIsRegex]: applicationCustomUrlIsRegex
        }

        const retVal = {
            [CpRuleSchema.source]: source,
            [CpRuleSchema.destination]: destination,
            [CpRuleSchema.serviceOrApplication]: serviceOrAppi,
            [CpRuleSchema.service]: service,
            [CpRuleSchema.application]: application,
            [CpRuleSchema.action]: action,
            [CpRuleSchema.log]: log,
            [CpRuleSchema.description]: description,
            [CpRuleSchema.enabled]: enabled,
        };
        if (retVal[CpRuleSchema.serviceOrApplication] !== ServiceOrAppiValues.SERVICE) {
            retVal[CpRuleSchema.service][serviceSchema.type] = ServiceTypeValues.ANY;
            retVal[CpRuleSchema.service][serviceSchema.customType] = ServiceCustomTypeValues.OTHER;
            retVal[CpRuleSchema.service][serviceSchema.ports] = '';
            retVal[CpRuleSchema.service][serviceSchema.icmpCode] = '';
            retVal[CpRuleSchema.service][serviceSchema.icmpType] = '';
            retVal[CpRuleSchema.service][serviceSchema.ipProtocol] = '';
        }
        if (retVal[CpRuleSchema.serviceOrApplication] !== ServiceOrAppiValues.APPI) {
            retVal[CpRuleSchema.application][applicationSchema.type] = ApplicationTypeValues.ANY;
            retVal[CpRuleSchema.application][applicationSchema.predefinedAppId] = '';
            retVal[CpRuleSchema.application][applicationSchema.customUrlValue] = '';
            retVal[CpRuleSchema.application][applicationSchema.customUrlName] = '';
            retVal[CpRuleSchema.application][applicationSchema.customUrlIsRegex] = '';
        }
        try {
            await Api.validateAccessPolicy(objectType, baseObjectName, retVal);

        } catch (e) {
            const errorMessage = getErrorMessage(e);
            enqueueErrorSnackbar(errorMessage, {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
            return;
        }
        onSuccess(retVal);
    }

    // On each new open, reset values
    useEffect(() => {
        if (isOpen) {
            setSourceType(SourceDestinationTypeValues.ANY);
            setSourceSingleIp('');
            setSourceStartIp('');
            setSourceEndIp('');
            setSourceNetworkIp('');
            setSourceNetworkSubnetMask('');
            setSourceZoneType(SourceDestinationZoneValues.USERS);
            setSourceSingleIpV6('');
            setSourceStartIpV6('');
            setSourceEndIpV6('');
            setSourceNetworkIpV6('');
            setSourceNetworkIpV6Prefix('');
            setSourceIpV6ObjectName('');
            setSourceUoCode('');
            setSourceDomain('');

            setDestinationType(sourceDestinationAny.sourceDestinationType);
            setDestinationSingleIp('');
            setDestinationDomain('');
            setDestinationStartIp('');
            setDestinationEndIp('');
            setDestinationNetworkIp('');
            setDestinationNetworkSubnetMask('');
            setDestinationSingleIpV6('');
            setDestinationStartIpV6('');
            setDestinationEndIpV6('');
            setDestinationNetworkIpV6('');
            setDestinationNetworkIpV6Prefix('');
            setDestinationIpV6ObjectName('');
            setDestinationUoCode('');
            setDestinationZoneType(SourceDestinationZoneValues.USERS);

            setServiceOrAppi(ServiceOrAppiValues.SERVICE);

            setServiceType(ServiceTypeValues.ANY);
            setServiceCustomType(ServiceCustomTypeValues.TCP);
            setServicePorts('');
            setServiceIcmpCode('0');
            setServiceIcmpType('0');
            setServiceIpProtocol('1');

            setApplicationType(ApplicationTypeValues.ANY);
            setApplicationPredefinedAppId('');
            setApplicationCustomUrlValue('');
            setApplicationCustomUrlName('');
            setApplicationCustomUrlIsRegex('');

            setAction(ActionValues.BLOCK);
            setLog(LogValues.LOG);
            setDescription('');
            setEnabled(true);
        }

        if (defaultObject) {
            if (defaultObject[CpRuleSchema.source]) {
                const source = defaultObject[CpRuleSchema.source];
                setSourceType(source[sourceDestinationSchema.type]);
                setSourceZoneType(source[sourceDestinationSchema.zone]);
                setSourceSingleIp(source[sourceDestinationSchema.ipSingle]);
                setSourceUoCode(source[sourceDestinationSchema.updatableObjectCode])
                setSourceStartIp(source[sourceDestinationSchema.ipStart]);
                setSourceEndIp(source[sourceDestinationSchema.ipEnd]);
                setSourceNetworkIp(source[sourceDestinationSchema.ipNetwork]);
                setSourceNetworkSubnetMask(source[sourceDestinationSchema.ipMask]);
                setSourceSingleIpV6(source[sourceDestinationSchema.ipV6Single]);
                setSourceStartIpV6(source[sourceDestinationSchema.ipV6Start]);
                setSourceEndIpV6(source[sourceDestinationSchema.ipV6End]);
                setSourceNetworkIpV6(source[sourceDestinationSchema.ipV6Network]);
                setSourceNetworkIpV6Prefix(source[sourceDestinationSchema.ipV6Prefix]);
                setSourceIpV6ObjectName(source[sourceDestinationSchema.ipV6ObjectName]);
                setSourceDomain(source[sourceDestinationSchema.domain]);
                setSourceDomainObject(source[sourceDestinationSchema.domainObject]);

            }
            if (defaultObject[CpRuleSchema.destination]) {
                const destination = defaultObject[CpRuleSchema.destination];
                setDestinationType(destination[sourceDestinationSchema.type]);
                setDestinationZoneType(destination[sourceDestinationSchema.zone]);
                setDestinationSingleIp(destination[sourceDestinationSchema.ipSingle]);
                setDestinationDomain(destination[sourceDestinationSchema.domain]);
                setDestinationDomainObject(destination[sourceDestinationSchema.domainObject]);
                setDestinationUoCode(destination[sourceDestinationSchema.updatableObjectCode])
                setDestinationStartIp(destination[sourceDestinationSchema.ipStart]);
                setDestinationEndIp(destination[sourceDestinationSchema.ipEnd]);
                setDestinationNetworkIp(destination[sourceDestinationSchema.ipNetwork]);
                setDestinationNetworkSubnetMask(destination[sourceDestinationSchema.ipMask]);
                setDestinationSingleIpV6(destination[sourceDestinationSchema.ipV6Single]);
                setDestinationStartIpV6(destination[sourceDestinationSchema.ipV6Start]);
                setDestinationEndIpV6(destination[sourceDestinationSchema.ipV6End]);
                setDestinationNetworkIpV6(destination[sourceDestinationSchema.ipV6Network]);
                setDestinationNetworkIpV6Prefix(destination[sourceDestinationSchema.ipV6Prefix]);
                setDestinationIpV6ObjectName(destination[sourceDestinationSchema.ipV6ObjectName]);
            }
            if (defaultObject[CpRuleSchema.serviceOrApplication]) {
                setServiceOrAppi(defaultObject[CpRuleSchema.serviceOrApplication]);
            }
            if (defaultObject[CpRuleSchema.service]) {
                const service = defaultObject[CpRuleSchema.service];
                setServiceType(service[serviceSchema.type]);
                setServiceCustomType(service[serviceSchema.customType]);
                setServicePorts(service[serviceSchema.ports]);
                setServiceIcmpType(service[serviceSchema.icmpType]);
                setServiceIcmpCode(service[serviceSchema.icmpCode]);
                setServiceIpProtocol(service[serviceSchema.ipProtocol]);
            }
            if (defaultObject[CpRuleSchema.application]) {
                const application = defaultObject[CpRuleSchema.application];
                setApplicationType(application[applicationSchema.type]);
                setApplicationPredefinedAppId(application[applicationSchema.predefinedAppId]);
                setApplicationCustomUrlValue(application[applicationSchema.customUrlValue]);
                setApplicationCustomUrlName(application[applicationSchema.customUrlName]);
                setApplicationCustomUrlIsRegex(application[applicationSchema.customUrlIsRegex]);
            }
            if (defaultObject[CpRuleSchema.action]) {
                setAction(defaultObject[CpRuleSchema.action]);
            }
            if (defaultObject[CpRuleSchema.log]) {
                setLog(defaultObject[CpRuleSchema.log]);
            }
            if (defaultObject[CpRuleSchema.description]) {
                setDescription(defaultObject[CpRuleSchema.description]);
            }
            if (defaultObject[CpRuleSchema.enabled]) {
                setEnabled(defaultObject[CpRuleSchema.enabled]);
            } else {
                setEnabled(false);
            }
        }
    }, [isOpen, defaultObject, sourceDestinationSchema, serviceSchema, applicationSchema, sourceDestinationAny]);

    const sourceUoName = useMemo(()=>{
        if(!sourceUoCode){
            return "";
        }
        let uoName="";
        if (uoTableData && uoTableData.length>0) {
            const UpdatableObjectCode = searchFunction(uoTableData,sourceUoCode);
                if (UpdatableObjectCode) {
                    uoName= UpdatableObjectCode.displayName;
                }
        }
        else{
            uoName= defaultObject.source.updatableObjectName;
        }
        return uoName;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uoTableData, sourceUoCode]);

    const destinationUoName = useMemo(() => {
        if(!destinationUoCode){
            return "";
        }

        let uoName="";
        if (uoTableData && uoTableData.length>0) {
            const UpdatableObjectCode = searchFunction(uoTableData,destinationUoCode);
            if (UpdatableObjectCode) {
                uoName= UpdatableObjectCode.displayName;
            }
        }
        else{
            uoName= defaultObject.destination.updatableObjectName;
        }
        return uoName;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uoTableData, destinationUoCode])

    const AccessPolicyNextGenBaseEditorModalServiceComponent = (serviceType) => {
        return (
            <>
                {
                    serviceType === ServiceTypeValues.CUSTOM && (
                        <SMCSelect
                            value={serviceCustomType}
                            onChange={e => setServiceCustomType(e.target.value)}
                            width={220}
                            label={t('SMP.TYPE')}
                        >
                            {serviceCustomTypeOptions}
                        </SMCSelect>
                    )
                }
                {
                    serviceType === ServiceTypeValues.CUSTOM && (
                        (() => {
                            switch (serviceCustomType) {
                                case ServiceCustomTypeValues.TCP:
                                case ServiceCustomTypeValues.UDP:
                                    return (
                                        <SMCTextInput
                                            width={220}
                                            value={servicePorts}
                                            onChange={e => setServicePorts(e.target.value)}
                                            label={"Ports"}
                                            tooltip={t('com.sofaware.db.INVALID_PORTS_FORMAT')}
                                        />
                                    );
                                case ServiceCustomTypeValues.ICMP:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={serviceIcmpType}
                                                onChange={e => setServiceIcmpType(e.target.value)}
                                                label={"ICMP type"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={serviceIcmpCode}
                                                onChange={e => setServiceIcmpCode(e.target.value)}
                                                label={"ICMP code"}
                                            />
                                        </>
                                    );
                                case ServiceCustomTypeValues.OTHER:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={serviceIpProtocol}
                                                onChange={e => setServiceIpProtocol(e.target.value)}
                                                label={"IP Protocol"}
                                            />
                                        </>
                                    );
                                default:
                                    return (
                                        <EmptyRow/>
                                    )
                            }
                        })()
                    )
                }
            </>
        )
    }

    return (
        <>
            <SMCModal
                isOpen={isOpen}
                onRequestClose={onCancel}
                width={width}
                height={height}
                title={isOutgoing ? t('SMP.PLAN.SEC_SOFT_BLADES.FW_WIZ_OUTGOING') : t('SMP.PLAN.SEC_SOFT_BLADES.FW_WIZ_INCOMING')}
            >
                <SMCModalBody>
                    {/* Source */}
                    <SMCSelect
                        value={sourceType}
                        onChange={e => setSourceType(e.target.value)}
                        width={220}
                        label={t('SMP.PLAN.SEC_SOFT_BLADES.SOURCE')}
                    >
                        {sourceOptions}
                    </SMCSelect>
                    {
                        (() => {
                            switch (sourceType) {
                                case SourceDestinationTypeValues.HOST:
                                    return (
                                        <SMCTextInput
                                            width={220}
                                            value={sourceSingleIp}
                                            onChange={e => setSourceSingleIp(e.target.value)}
                                            label="IPv4 address"
                                        />
                                    );
                                case SourceDestinationTypeValues.IP_RANGE:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={sourceStartIp}
                                                onChange={e => setSourceStartIp(e.target.value)}
                                                label={"Start IPv4"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceEndIp}
                                                onChange={e => setSourceEndIp(e.target.value)}
                                                label={"End IPv4"}
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.NETWORK:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={sourceNetworkIp}
                                                onChange={e => setSourceNetworkIp(e.target.value)}
                                                label="Network IPv4 address"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceNetworkSubnetMask}
                                                onChange={e => setSourceNetworkSubnetMask(e.target.value)}
                                                label="Subnet mask"
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.ZONE:
                                    return (
                                        <SMCSelect
                                            width={220}
                                            value={sourceZoneType}
                                            onChange={e => setSourceZoneType(e.target.value)}
                                            label={t('SMP.PLAN.SEC_SOFT_BLADES.ZONE.TYPE')}
                                        >
                                            {zoneTypeOptions}
                                        </SMCSelect>
                                    );
                                case SourceDestinationTypeValues.IPV6_HOST:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={sourceSingleIpV6}
                                                onChange={e => setSourceSingleIpV6(e.target.value)}
                                                label={"IPv6 address"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceIpV6ObjectName}
                                                onChange={e => setSourceIpV6ObjectName(e.target.value)}
                                                label={"IPv6 Object Name"}
                                                tooltip={t('com.sofaware.db.OBJECT_NAME_TOOLTIP')}
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.IPV6_RANGE:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={sourceStartIpV6}
                                                onChange={e => setSourceStartIpV6(e.target.value)}
                                                label={"Start IPv6"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceEndIpV6}
                                                onChange={e => setSourceEndIpV6(e.target.value)}
                                                label={"End IPv6"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceIpV6ObjectName}
                                                onChange={e => setSourceIpV6ObjectName(e.target.value)}
                                                label={"IPv6 Object Name"}
                                                tooltip={t('com.sofaware.db.OBJECT_NAME_TOOLTIP')}
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.IPV6_NETWORK:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={sourceNetworkIpV6}
                                                onChange={e => setSourceNetworkIpV6(e.target.value)}
                                                label="Network IPv6 address"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceNetworkIpV6Prefix}
                                                onChange={e => setSourceNetworkIpV6Prefix(e.target.value)}
                                                label="Network IPv6 prefix"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceIpV6ObjectName}
                                                onChange={e => setSourceIpV6ObjectName(e.target.value)}
                                                label={"IPv6 Object Name"}
                                                tooltip={t('com.sofaware.db.OBJECT_NAME_TOOLTIP')}
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.DOMAIN:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={sourceDomain}
                                                onChange={e => setSourceDomain(e.target.value)}
                                                label="Domain"
                                                placeholder="For example, mysite.com"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={sourceDomainObject}
                                                onChange={e => setSourceDomainObject(e.target.value)}
                                                label="Domain Object Name"
                                                placeholder="For example, mysite."
                                                tooltip={t('com.sofaware.db.INVALID_DOMAIN_OBJECT_NAME')}
                                            /> </>
                                    );
                                case SourceDestinationTypeValues.UPDATABLE_OBJECTS:
                                    return (
                                        <RowLayout>
                                            <SMCTextInput
                                                disabled
                                                value={sourceUoName}
                                                width={220}
                                                label={"Updatable Objects"}
                                                inline
                                                tooltip={t('com.sofaware.db.UPDATABLE_OBJECT_WARNING') + '\n' + t('com.sofaware.db.SOURCE_DOMAIN_WARNING')}
                                            />
                                            <SMCLink
                                                onClick={() => {
                                                    setIsUoEditingSourceOrDestination("SOURCE");
                                                    openUoSelection();
                                                }}
                                            >
                                                {sourceUoCode ? 'Edit' : 'New'}
                                            </SMCLink>
                                        </RowLayout>
                                    );
                                default:
                                    return (
                                        <EmptyRow/>
                                    )
                            }
                        })()
                    }
                    <EmptyRow/>

                    {/* Destination */}
                    <SMCSelect
                        value={destinationType}
                        onChange={e => setDestinationType(e.target.value)}
                        width={220}
                        label={t('SMP.PLAN.SEC_SOFT_BLADES.DESTINATION')}
                    >
                        {destinationOptions}
                    </SMCSelect>
                    {
                        (() => {
                            switch (destinationType) {
                                case SourceDestinationTypeValues.HOST:
                                    return (
                                        <SMCTextInput
                                            width={220}
                                            value={destinationSingleIp}
                                            onChange={e => setDestinationSingleIp(e.target.value)}
                                            label="IPv4 address"
                                        />
                                    );
                                case SourceDestinationTypeValues.DOMAIN:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={destinationDomain}
                                                onChange={e => setDestinationDomain(e.target.value)}
                                                label="Domain"
                                                placeholder="For example, mysite.com"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationDomainObject}
                                                onChange={e => setDestinationDomainObject(e.target.value)}
                                                label="Domain Object Name"
                                                placeholder="For example, mysite"
                                                tooltip={t('com.sofaware.db.INVALID_DOMAIN_OBJECT_NAME')}
                                            /> </>
                                    );
                                case SourceDestinationTypeValues.UPDATABLE_OBJECTS:
                                    return (
                                        <RowLayout>
                                            <SMCTextInput
                                                disabled
                                                value={destinationUoName}
                                                width={220}
                                                label={"Updatable Objects"}
                                                inline
                                                tooltip={t('com.sofaware.db.UPDATABLE_OBJECT_WARNING') + '\n' + t('com.sofaware.db.SOURCE_DOMAIN_WARNING')}
                                            />
                                            <SMCLink
                                                onClick={() => {
                                                    setIsUoEditingSourceOrDestination("DESTINATION");
                                                    openUoSelection();
                                                }}
                                            >
                                                {destinationUoName ? 'Edit' : 'New'}
                                            </SMCLink>
                                        </RowLayout>
                                    );
                                case SourceDestinationTypeValues.ZONE:
                                    return (
                                        <SMCSelect
                                            width={220}
                                            value={destinationZoneType}
                                            onChange={e => setDestinationZoneType(e.target.value)}
                                            label={t('SMP.PLAN.SEC_SOFT_BLADES.ZONE.TYPE')}
                                        >
                                            {zoneTypeOptions}
                                        </SMCSelect>
                                    );
                                case SourceDestinationTypeValues.IP_RANGE:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={destinationStartIp}
                                                onChange={e => setDestinationStartIp(e.target.value)}
                                                label="Start IPv4"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationEndIp}
                                                onChange={e => setDestinationEndIp(e.target.value)}
                                                label="End IPv4"
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.NETWORK:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={destinationNetworkIp}
                                                onChange={e => setDestinationNetworkIp(e.target.value)}
                                                label="Network IPv4 address"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationNetworkSubnetMask}
                                                onChange={e => setDestinationNetworkSubnetMask(e.target.value)}
                                                label="Subnet mask"
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.IPV6_HOST:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={destinationSingleIpV6}
                                                onChange={e => setDestinationSingleIpV6(e.target.value)}
                                                label={"IPv6 address"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationIpV6ObjectName}
                                                onChange={e => setDestinationIpV6ObjectName(e.target.value)}
                                                label={"IPv6 Object Name"}
                                                tooltip={t('com.sofaware.db.OBJECT_NAME_TOOLTIP')}
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.IPV6_RANGE:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={destinationStartIpV6}
                                                onChange={e => setDestinationStartIpV6(e.target.value)}
                                                label={"Start IPv6"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationEndIpV6}
                                                onChange={e => setDestinationEndIpV6(e.target.value)}
                                                label={"End IPv6"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationIpV6ObjectName}
                                                onChange={e => setDestinationIpV6ObjectName(e.target.value)}
                                                label={"IPv6 Object Name"}
                                                tooltip={t('com.sofaware.db.OBJECT_NAME_TOOLTIP')}
                                            />
                                        </>
                                    );
                                case SourceDestinationTypeValues.IPV6_NETWORK:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={destinationeNetworkIpV6}
                                                onChange={e => setDestinationNetworkIpV6(e.target.value)}
                                                label="Network IPv6 address"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationNetworkIpV6Prefix}
                                                onChange={e => setDestinationNetworkIpV6Prefix(e.target.value)}
                                                label="Network IPv6 prefix"
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={destinationIpV6ObjectName}
                                                onChange={e => setDestinationIpV6ObjectName(e.target.value)}
                                                label={"IPv6 Object Name"}
                                                tooltip={t('com.sofaware.db.OBJECT_NAME_TOOLTIP')}
                                            />
                                        </>
                                    );
                                default:
                                    return (
                                        <EmptyRow/>
                                    );
                            }
                        })()
                    }
                    <EmptyRow/>

                    {/* Service */}
                    {isOutgoing ?
                        <>
                            <RowLayout>
                                <SMCRadio
                                    labelWidth={160}
                                    checked={serviceOrAppi === ServiceOrAppiValues.SERVICE}
                                    label={t('SMP.PLAN.SEC_SOFT_BLADES.SERVICE')}
                                    onChange={e => setServiceOrAppi(ServiceOrAppiValues.SERVICE)}
                                />
                                <SMCSelect
                                    value={serviceType}
                                    disabled={serviceOrAppi !== ServiceOrAppiValues.SERVICE}
                                    onChange={e => setServiceType(e.target.value)}
                                    width={220}
                                >
                                    {serviceTypeOptions}
                                </SMCSelect>
                            </RowLayout>
                            <SMCDisabledProvider forceDisable={serviceOrAppi !== ServiceOrAppiValues.SERVICE}>
                                {AccessPolicyNextGenBaseEditorModalServiceComponent(serviceType)}
                            </SMCDisabledProvider>
                        </>
                        :
                        //Is Incoming
                        <>
                            <SMCSelect
                                value={serviceType}
                                onChange={e => setServiceType(e.target.value)}
                                width={220}
                                label={t('SMP.PLAN.SEC_SOFT_BLADES.SERVICE')}
                            >
                                {serviceTypeOptions}
                            </SMCSelect>
                            {AccessPolicyNextGenBaseEditorModalServiceComponent(serviceType)}
                        </>
                    }
                    <EmptyRow/>

                    {/* Application */}
                    {isOutgoing &&
                        <>
                            <RowLayout>
                                <SMCRadio
                                    labelWidth={160}
                                    checked={serviceOrAppi === ServiceOrAppiValues.APPI}
                                    label={t('SMP.PLAN.SEC_SOFT_BLADES.APPLICATION')}
                                    onChange={e => setServiceOrAppi(ServiceOrAppiValues.APPI)}
                                />
                                <SMCSelect
                                    value={applicationType}
                                    disabled={serviceOrAppi !== ServiceOrAppiValues.APPI}
                                    onChange={e => setApplicationType(e.target.value)}
                                    width={220}

                                >
                                    {applicationTypeOptions}
                                </SMCSelect>
                            </RowLayout>
                            <SMCDisabledProvider forceDisable={serviceOrAppi !== ServiceOrAppiValues.APPI}>
                                {
                                    applicationType === ApplicationTypeValues.PREDEFINED && (
                                        <RowLayout>
                                            <SMCTextInput
                                                disabled
                                                value={applicationName}
                                                width={220}
                                                label={t("SMP.PLAN.SEC_SOFT_BLADES.PREDEFIND")}
                                                inline
                                            />
                                            <SMCLink
                                                onClick={openAppiEditor}
                                            >
                                                {applicationPredefinedAppId ? 'Edit' : 'New'}
                                            </SMCLink>
                                        </RowLayout>
                                    )
                                }
                                {
                                    applicationType === ApplicationTypeValues.CUSTOM_URL && (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={applicationCustomUrlValue}
                                                onChange={e => setApplicationCustomUrlValue(e.target.value)}
                                                label={"URL"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={applicationCustomUrlName}
                                                onChange={e => setApplicationCustomUrlName(e.target.value)}
                                                label={"Name"}
                                                tooltip={t('com.sofaware.db.OBJECT_NAME_TOOLTIP')}
                                            />
                                            <SMCCheckbox
                                                checked={applicationCustomUrlIsRegex}
                                                onChange={e => setApplicationCustomUrlIsRegex(e.target.checked)}
                                                label={t("SMP.PLAN.SEC_SOFT_BLADES.CUSTOM_URL_REGEX")}
                                                tooltip={t("SMP.PLAN.SEC_SOFT_BLADES.CUSTOM_URL_REGEX_NOTE")}
                                            />
                                        </>
                                    )
                                }
                            </SMCDisabledProvider>
                        </>
                    }
                    <EmptyRow/>

                    <SMCSelect
                        width={220}
                        value={action}
                        onChange={e => setAction(e.target.value)}
                        label={t("SMP.ACTION")}
                    >
                        <SMCOption value={ActionValues.ACCEPT}>{t('SMP.PLAN.SEC_SOFT_BLADES.ACCEPT')}</SMCOption>
                        <SMCOption value={ActionValues.BLOCK}>{t('SMP.PLAN.SEC_SOFT_BLADES.BLOCK')}</SMCOption>
                    </SMCSelect>

                    <SMCSelect
                        width={220}
                        value={log}
                        onChange={e => setLog(e.target.value)}
                        label={t("SMP.LOG")}
                    >
                        <SMCOption value={LogValues.NONE}>{t('SMP.NONE')}</SMCOption>
                        <SMCOption value={LogValues.LOG}>{t('SMP.LOG')}</SMCOption>
                        <SMCOption value={LogValues.ALERT}>{t('SMP.ALERT')}</SMCOption>
                        <SMCOption value={LogValues.ACCOUNT}>{t('SMP.PLAN.SEC_SOFT_BLADES.ACCOUNT')}</SMCOption>
                    </SMCSelect>

                    <SMCTextInput
                        width={220}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        label={t('SMP.DESCRIPTION')}
                    />

                    <SMCCheckbox
                        checked={enabled}
                        onChange={e => setEnabled(e.target.checked)}
                        label={t('SMP.ENABLED')}
                    />

                </SMCModalBody>
                <SMCModalButtonContainer>
                    <SMCButton onClick={onFinish}>
                        <FontAwesomeIcon style={{marginTop: '3px', marginRight: '5px'}} color="green" icon="check"/>
                        {t('SMC_GEN_FINISH')}
                    </SMCButton>
                    <SMCButton onClick={onCancel}>
                        <FontAwesomeIcon style={{marginTop: '3px', marginRight: '5px'}} color="grey" icon="times"/>
                        {t('SMC_GEN_CANCEL')}
                    </SMCButton>
                </SMCModalButtonContainer>
            </SMCModal>

            {/* Floating windowns */}
            {isOutgoing && isAppiEditorOpen &&
                <AppiEditorModal
                    isOpen={isAppiEditorOpen}
                    onRequestClose={closeAppiEditor}
                    deafultSelected={applicationPredefinedAppId ? [applicationPredefinedAppId] : []}
                    onSuccess={newAppiBlockList => {
                        if (newAppiBlockList.length) {
                            setApplicationPredefinedAppId(newAppiBlockList[0]);
                        } else {
                            setApplicationPredefinedAppId(''); // If chosen list is empty, set id as empty string
                        }
                        closeAppiEditor();
                    }}
                    appType="ACCESS_POLICY"
                    selectMultiple={false}
                    setAppiDataValue={setAppiDataValue}
                />
            }

            {isUoSelectionOpen && <UpdatableObjectSelectModal
                isOpen={isUoSelectionOpen}
                onRequestClose={closeUoSelection}
                deafultSelected={(isUoEditingSourceOrDestination === "SOURCE") ? (sourceUoCode ? [sourceUoCode] : []) : (destinationUoCode ? [destinationUoCode] : [])}
                onSuccess={newUoList => {
                    if (newUoList.length) {
                        if (isUoEditingSourceOrDestination === "SOURCE") {
                            setSourceUoCode(newUoList[0]);
                        } else {
                            setDestinationUoCode(newUoList[0]);
                        }
                    } else {
                        if (isUoEditingSourceOrDestination === "SOURCE") {
                            setSourceUoCode("");
                        } else {
                            setDestinationUoCode("");
                        }
                    }
                    closeUoSelection();
                }}
                selectMultiple={false}
                setUoTableDataValue={setUoTableDataValue}
            />}
        </>
    );
}

export default AccessPolicyNextGenBaseEditorModal;
